/* eslint-disable react/no-array-index-key */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import styled, { ThemeProvider } from 'styled-components';
import WebFontLoaderFactory from '../../../helpers/WebFontLoaderFactory';
import ConstantlyMovingBar from '../animated/views/ConstantlyMovingBar';
import AnimationControls from '../animated/views/AnimationControls';
import Branding from '../views/Branding';
import { styledUI } from '../../sm/StyledUI';
import hexToRgba from '../../../helpers/hexToRgba';

const defTheme = require('./SplitImageTPLDefaultTheme');

// const composeGradient = props => {
//     if (props.gradient === 'radial') {
//         return `${props.gradient}-gradient(ellipse farthest-corner at ${props.position}, ${props.startColor} 0%,  ${
//             props.endColor
//         } 100%)`;
//     } else {
//         return `${props.gradient}-gradient(${props.position}, ${props.startColor} 0%,  ${props.endColor} 100%)`;
//     }
// };

const layerToStyle = (layer, i) => {
    return {
        backgroundImage: `url(${layer.backgroundImage})`,
        backgroundSize: `${layer.backgroundSize || 'initial'}`,
        zIndex: i + 1,
        transform: `scale(${layer.scale || 1.0}) translate(${layer.translateX || 0}%, ${layer.translateY || 0}%)`,
        '-webkit-transform': `scale(${layer.scale || 1.0}) translate(${layer.translateX || 0}%, ${layer.translateY ||
            0}%)`,

        opacity: `${layer.opacity || 1.0}`,
        backgroundPosition: `${layer.backgroundPositionX} ${layer.backgroundPositionY}`,
        backgroundRepeat: 'no-repeat',
    };
};

const generateLayerClass = (layer, i, theme, teamIdColor) => {
    const isTeamColor = layer.backgroundImage.indexOf('isTeamColor') > -1 && teamIdColor;
    const noTeamColor = layer.backgroundImage.indexOf('isTeamColor') > -1 && !teamIdColor;
    const isGradient = layer.backgroundImage.indexOf('gradient.svg') > -1;
    let bgrPostition = 'center center';
    const transform = [`scale(${layer.scale || 1.0})`];
    if (layer.backgroundSize === 'cover') bgrPostition = `${layer.translateX}% ${layer.translateY}%`;

    if (layer.backgroundSize === 'contain' || layer.backgroundSize === 'initial') {
        let bgrX = layer.backgroundPositionX;
        let bgrY = layer.backgroundPositionY;

        if (layer.backgroundPositionX === 'moveX') {
            bgrX = `center`;
            transform.push([`translateX(${layer.translateX}px)`]);
        }
        if (layer.backgroundPositionY === 'moveY') {
            bgrY = `center`;
            transform.push([`translateY(${layer.translateY}px)`]);
        }

        bgrPostition = `${bgrX} ${bgrY}`;
    }

    return `
        .wdp-bgr-${i} {
            &:after {
                content: 'layer ${i}';
                position: absolute;
                right: 10;
                top: ${(i + 1) * 10};
                display: none;
            }
            position: absolute;
            display: block;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            background-image: url("${layer.backgroundImage}");
            ${noTeamColor ? `background-image: none; ` : ''}
            ${
                isTeamColor
                    ? `background-image: url("${layer.backgroundImage}&fromColor=${hexToRgba(
                          theme.teamBackgroundColor,
                      )}&toColor=${hexToRgba(theme.teamBackgroundColor)}");`
                    : ''
            }
            background-size: ${layer.backgroundSize || 'cover'};
            background-position: ${bgrPostition};
            background-repeat: no-repeat;
            z-index: ${i + 1};
            transform: ${transform.join(' ')};
            opacity: ${layer.opacity || 1.0};

            ${
                isGradient
                    ? `left: ${theme.content.gradientAlign === 'right' ? 'auto' : 0}; top: ${
                          theme.content.gradientAlign === 'bottom' ? 'auto' : 0
                      }; width: ${theme.content.gradientWidth || 0}%; height: ${theme.content.gradientHeight || 0}%`
                    : ''
            }

            }
    `;
};

const Wrapper = styled.div`
    width: ${props => (_.isInteger(props.wcwidth) ? `${props.wcwidth}px` : `800px`)};
    position: relative;
    overflow: hidden;
    font-family: ${props => props.theme.fonts.main.fontFamily};
    font-style: ${props => props.theme.fonts.main.fontStyle};
    text-transform: ${props => props.theme.fonts.main.textTransform};

    &:hover {
        .animation-controls {
            opacity: 1;
        }
    }

    table {
        font-style: inherit;
        color: inherit;
    }

    color: ${props => props.theme.color};

    .wdp-bgr {
        position: absolute;
        display: block;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        pointer-events: none;
    }
    .wdp-bgr-custom {
        pointer-events: all;
    }

    ${props =>
        props.widgetColors &&
        Object.keys(props.widgetColors).reduce((tot, cur) => {
            const color = props.widgetColors[`${cur}`];
            let property = 'color';
            if (_.get(cur.split('__c__'), '[1][0]') === 'b') property = 'background-color';

            //for pseudo bg
            if (_.get(cur.split('__c__'), '[1][0]') === 'a') {
                tot += `.${cur} {${property}: ${color}}}`;
                return tot;
            }

            if (property === 'color') {
                tot += `.${cur} {${property}: ${color} !important; * {${property}: ${color} !important;}}`;
            } else {
                tot += `.${cur} {${property}: ${color}!important;}`;
            }

            return tot;
        }, ``)};

    ${props =>
        (props.theme.layers || []).map(
            (layer, i) => layer && generateLayerClass(layer, i, props.theme, props.teamIdColor),
        )};
`;

/** CONTAINERS **/
const Container = styled.div`
    display: flex;
    -webkit-flex-flow: row wrap;

    z-index: 1;
    overflow: hidden;
    justify-content: center;
    min-height: ${props =>
        (props.minHeight && props.minHeight + 'px') ||
        (props.theme.content.minHeight && props.theme.content.minHeight + 'px') ||
        'auto'};
`;
const TwoColumnContainer = styled(Container)`
    flex-direction: row;
    position: relative;
`;

//pointer-events: ${props => (props.theme.editing ? 'all' : 'none')};
const OneColumnContainer = styled(Container)`
    position: relative;
    flex-direction: column;
    padding: 0;
    pointer-events: none;

    .widget-customizer & {
        pointer-events: all;
    }
`;

const BackgroundContainer = styled(Container)`
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 0;
    align-items: stretch;
`;

/** BLOCKS **/

const PrimaryBlock = styled.div`
    overflow: hidden;
    padding: ${props => props.theme.contentPadding}px;
    flex-grow: ${props => (props.weight || props.theme.weight) * 10};
    flex-basis: 0;
    display: flex;
    flex-direction: column;

    ${props => props.reverse && `flex-direction: column-reverse;`};
`;

const SingleBlock = styled.div`
    overflow: hidden;
    padding: ${props => props.theme.contentPadding}px;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    ${props => props.reverse && `flex-direction: column-reverse;`};
    margin: ${props => props.margin != undefined && props.margin};
    padding: ${props => props.padding != undefined && props.padding};
    position: relative;
`;

const SecondaryBlock = styled.div`
    position: relative;
    padding: ${props => props.theme.contentPadding}px;
    overflow: hidden;
    z-index: 2;
    flex-grow: ${props => (1 - (props.weight || props.theme.weight)) * 10};
    flex-basis: 0;
    display: flex;
    flex-direction: column;

    .secondary-block-title {
        text-align: left !important;
    }
    background: ${props => props.theme.content.backgroundColor};
`;

const Content = styled.div`
    position: ${props => props.position || 'unset'};
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: ${props => props.valign || props.theme.content.valign || 'top'};
    z-index: 2;

    margin: ${props => {
        if (props.margin != undefined) {
            return props.margin;
        }

        return props.theme.content.margin.replace(/([1-9][0-9]+)/g, '$1px');
    }};
    background: ${props => props.theme.content.fillColor || 'transparent'};
    padding: ${props => {
        if (props.padding != undefined) {
            return props.padding;
        }

        return props.theme.content.padding.replace(/([1-9][0-9]+)/g, '$1px');
    }};
`;

class SplitImageTPL extends React.Component {
    static propTypes = {
        social: PropTypes.bool,
        theme: PropTypes.object,
        pattern: PropTypes.string,
        secondaryContent: PropTypes.object,
        primaryContent: PropTypes.object,

        contentProps: PropTypes.object,
        secondaryContentProps: PropTypes.object,
        title: PropTypes.string,
        preview: PropTypes.object,
    };

    static defaultProps = {
        theme: defTheme,
        social: true,
    };

    constructor(props) {
        super(props);

        const { theme } = props;

        this.state = {
            theme,
        };
    }

    componentDidMount = () => {
        window.addEventListener('startAnimation', this.startAnimationListener, false);
        this.reloadFont([this.state.theme.googleFontName]);
    };

    componentWillReceiveProps = nextProps => {
        const { theme } = nextProps;
        if (this.state.theme.googleFontName !== theme.googleFontName) {
            this.reloadFont([theme.googleFontName]);
        }
        this.setState({
            theme: theme,
        });
    };

    componentWillUnmount = () => {
        window.removeEventListener('startAnimation', this.startAnimationListener, false);
    };

    startAnimationListener = () => {
        this.setState({ pause: false });
    };

    reloadFont = fonts => {
        if (_.isEmpty(fonts)) return;
        const WFL = WebFontLoaderFactory.init(window);
        WFL.load({
            google: {
                families: fonts,
            },
            active: () => {},
        });
    };

    renderTopLevelLayers = () => {
        const { theme } = this.props;
        return theme.layers.map((layer, i) => {
            return layer && layer.topOverlay && <span key={`wdp-bgr-${i}`} className={`wdp-bgr wdp-bgr-${i}`} />;
        });
    };

    renderBackgroundLayers = () => {
        const { theme, bgrLayer, previewLayer, background } = this.props;

        if (theme.layers.length > 0 && bgrLayer && _.isObject(bgrLayer) && bgrLayer.backgroundImage !== '') {
            theme.layers[0] = bgrLayer;
        }

        return [
            background && (
                <span key="wdp-bgr-custom" className="wdp-bgr wdp-bgr-custom">
                    {background}
                </span>
            ),
            !background &&
                theme.layers.map((layer, i) => {
                    return (
                        layer && !layer.topOverlay && <span key={`wdp-bgr-${i}`} className={`wdp-bgr wdp-bgr-${i}`} />
                    );
                }),

            previewLayer && (
                <span
                    key="preview-layer"
                    className={`wdp-bgr wdp-bgr-${theme.layers.length + 3}`}
                    style={layerToStyle(previewLayer, theme.layers.length + 3)}
                />
            ),
        ];
    };

    widgetCanNotBeRenderedMessage = () => {
        const { contentProps } = this.props;
        const { valign } = contentProps;

        const { theme } = this.state;

        return (
            <ThemeProvider theme={theme}>
                <Wrapper className="sm-wdp" wcwidth={800}>
                    {/* BACKGROUND */}
                    <BackgroundContainer>{this.renderBackgroundLayers()}</BackgroundContainer>

                    <OneColumnContainer {...contentProps}>
                        <SingleBlock valign={valign}>
                            <Content {...contentProps}>
                                <div style={{ fontSize: 40, fontWeight: 700, margin: 'auto' }}>
                                    Widget can not be rendered as plain HTML
                                </div>
                            </Content>
                        </SingleBlock>
                    </OneColumnContainer>

                    {this.renderTopLevelLayers()}
                </Wrapper>
            </ThemeProvider>
        );
    };

    renderOneColumn = () => {
        let {
            primaryContent,

            contentProps = {},

            widgetProps,
            brandingPrimary,
            brandingSecondary,
            brandingAdditionalContent,
            screencast,
            brandingProps = {},
        } = this.props;
        const { theme } = this.state;
        const { disableBranding, valign } = contentProps;
        const { video } = widgetProps;

        brandingPrimary = brandingPrimary || theme.defaultBranding.brandingPrimary;
        brandingSecondary = brandingSecondary || theme.defaultBranding.brandingSecondary;

        const _brandingProps = {
            ...theme.brandingProps,
            ...brandingProps,
            additionalContent: brandingAdditionalContent,
        };
        if (disableBranding) _brandingProps.visibility = 'hidden';

        const Title = styledUI.getCustomComponent({ theme }, 'title', this.props.widgetProps);

        return (
            <ThemeProvider theme={theme}>
                <Wrapper
                    className="sm-wdp"
                    widgetColors={
                        theme.customColors ||
                        this.state.theme.widgetsColors[
                            _.get(this, 'props.widgetProps.name', _.get(this, 'props.widgetProps.widget'))
                        ]
                    }
                    teamIdColor={this.props.teamIdColor}
                    wcwidth={widgetProps.wcwidth}
                >
                    {/* BACKGROUND */}
                    <BackgroundContainer>{this.renderBackgroundLayers()}</BackgroundContainer>

                    <OneColumnContainer
                        style={{ visibility: video ? 'hidden' : 'visible' }}
                        className="sm-wdp__container"
                        {...contentProps}
                    >
                        <SingleBlock valign={valign} reverse={_brandingProps.position === 'top'}>
                            <Content {...contentProps}>
                                <Title
                                    titleImage={this.props.titleImage}
                                    title={this.props.title}
                                    subTitle={this.props.subTitle}
                                />
                                {primaryContent}
                            </Content>
                            <Branding {..._brandingProps}>
                                {brandingPrimary}
                                {brandingSecondary}
                            </Branding>
                        </SingleBlock>
                    </OneColumnContainer>
                    {this.renderTopLevelLayers()}
                    <AnimationControls
                        {..._.pick(this.props.widgetProps, [
                            'screencast',
                            'startAnimation',
                            'video',
                            'noAnimationControls',
                        ])}
                    />
                    <ConstantlyMovingBar screencast={screencast} />
                </Wrapper>
            </ThemeProvider>
        );
    };

    renderSpitImage = () => {
        let {
            primaryContent,
            secondaryContent,

            contentProps = {},
            secondaryContentProps,
            brandingPrimary,
            brandingSecondary,
            widgetProps,
            brandingAdditionalContent,
            screencast,
            brandingProps = {},
        } = this.props;
        const { theme } = this.state;
        const { disableBranding, weight, valign } = contentProps;
        const { video } = widgetProps;

        brandingPrimary = brandingPrimary || theme.defaultBranding.brandingPrimary;
        brandingSecondary = brandingSecondary || theme.defaultBranding.brandingSecondary;

        const Title = styledUI.getCustomComponent({ theme }, 'title', this.props.widgetProps);
        const _brandingProps = {
            ...theme.brandingProps,
            ...brandingProps,
            additionalContent: brandingAdditionalContent,
        };

        return (
            <ThemeProvider theme={theme}>
                <Wrapper
                    className="sm-wdp"
                    widgetColors={
                        theme.customColors ||
                        this.state.theme.widgetsColors[
                            _.get(this, 'props.widgetProps.name', _.get(this, 'props.widgetProps.widget'))
                        ]
                    }
                    wcwidth={widgetProps.wcwidth}
                >
                    <BackgroundContainer>{this.renderBackgroundLayers()}</BackgroundContainer>
                    <TwoColumnContainer
                        style={{ visibility: video ? 'hidden' : 'visible' }}
                        className="sm-wdp__container"
                        {...contentProps}
                    >
                        <SecondaryBlock {...secondaryContentProps} weight={weight}>
                            <Title
                                className="secondary-block-title"
                                titleImage={this.props.titleImage}
                                title={this.props.title}
                                subTitle={this.props.subTitle}
                            />
                            <Content {...contentProps}>{secondaryContent}</Content>
                            {!disableBranding && <Branding {...{ alignment: 'center' }}>{brandingSecondary}</Branding>}
                        </SecondaryBlock>
                        <PrimaryBlock weight={weight} valign={valign} reverse={_brandingProps.position === 'top'}>
                            <Content {...contentProps}>{primaryContent}</Content>
                            {!disableBranding && <Branding {..._brandingProps}>{brandingPrimary}</Branding>}
                        </PrimaryBlock>
                    </TwoColumnContainer>
                    {this.renderTopLevelLayers()}
                    <AnimationControls
                        {..._.pick(this.props.widgetProps, [
                            'screencast',
                            'startAnimation',
                            'video',
                            'smBrowser',
                            'noAnimationControls',
                        ])}
                    />
                    <ConstantlyMovingBar screencast={screencast} />
                </Wrapper>
            </ThemeProvider>
        );
    };

    render = () => {
        // const { video, output } = this.props.widgetProps;
        // if (output === 'sm' && video) return this.widgetCanNotBeRenderedMessage();
        const oneColumn = this.props.secondaryContent.props.empty;
        if (oneColumn) return this.renderOneColumn();
        return this.renderSpitImage();
    };
}

SplitImageTPL.spec = {};

export default SplitImageTPL;
export { defTheme as theme, SplitImageTPL };



/** WEBPACK FOOTER **
 ** ../src/widgets/components/sm/templates/SplitImageTPL.js
 **/